import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '@/shared/lib/format'

interface Sample {
  at: number
  value: number
}

const MAX_SAMPLES = 40
const CHART_HEIGHT = 44
const CHART_WIDTH = 320

/**
 * Faol sessiyalar soni — vaqt bo'yicha, JONLI.
 *
 * ⚠️ BU FABRIKATSIYA EMAS: `report` modulida trafik tarixi HTTP orqali
 * umuman berilmaydi (`DashboardPage.tsx` dagi eski izoh), ya'ni "bugungi
 * trafik" grafigini chizishning ISHONCHLI manbai yo'q. Bu grafik esa
 * BOSHQA narsa — u FreeRADIUS'dan har 15 soniyada haqiqatan kelayotgan
 * (`sessionApi.fetchSessionStats`) qiymatni shu ekranda ochilgan vaqtdan
 * boshlab yig'adi. Sahifa yopilsa tarix yo'qoladi — bu tarixiy hisobot
 * emas, "hozir nima bo'lyapti" ko'rinishi.
 */
export function ActiveSessionsTrend({ value }: { value: number | undefined }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [samples, setSamples] = useState<Sample[]>([])
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    if (value === undefined) return
    setSamples((prev) => {
      const next = [...prev, { at: Date.now(), value }]
      return next.length > MAX_SAMPLES ? next.slice(next.length - MAX_SAMPLES) : next
    })
    // `value` o'zgarmagan taqdirda ham har pollingda YANGI son sifatida
    // kelmasligi mumkin (masalan doim 3 ta sessiya) — bu holda nuqta
    // qo'shilmaydi va chiziq tekis qoladi. Bu XATO EMAS: qiymat haqiqatan
    // ham o'zgarmagan, tekis chiziq shuni to'g'ri ko'rsatadi.
  }, [value])

  const { path, areaPath, points, minValue, maxValue } = useMemo(() => {
    if (samples.length < 2) {
      return { path: '', areaPath: '', points: [] as { x: number; y: number }[], minValue: 0, maxValue: 0 }
    }
    const values = samples.map((s) => s.value)
    const min = Math.min(...values)
    const max = Math.max(...values)
    // Barcha qiymat bir xil bo'lsa ham chiziq TEKIS bo'lib qolmasin uchun
    // sun'iy diapazon — aks holda chiziq grafik o'rtasida yassi ko'rinardi
    const range = max - min || 1
    const pad = 6

    const pts = samples.map((s, i) => {
      const x = (i / (samples.length - 1)) * (CHART_WIDTH - pad * 2) + pad
      const y = CHART_HEIGHT - pad - ((s.value - min) / range) * (CHART_HEIGHT - pad * 2)
      return { x, y }
    })

    const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ')
    const area = `${linePath} L ${pts[pts.length - 1]?.x.toFixed(1)} ${CHART_HEIGHT} L ${pts[0]?.x.toFixed(1)} ${CHART_HEIGHT} Z`

    return { path: linePath, areaPath: area, points: pts, minValue: min, maxValue: max }
  }, [samples])

  if (samples.length < 2) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('dashboard.trendGathering')}
      </Typography.Text>
    )
  }

  const hovered = hoverIndex !== null ? samples[hoverIndex] : null
  const hoveredPoint = hoverIndex !== null ? points[hoverIndex] : null

  const handleMove = (e: ReactMouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    const ratio = (e.clientX - rect.left) / rect.width
    const index = Math.round(ratio * (samples.length - 1))
    setHoverIndex(Math.max(0, Math.min(samples.length - 1, index)))
  }

  return (
    <div style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        width="100%"
        height={CHART_HEIGHT}
        preserveAspectRatio="none"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIndex(null)}
        role="img"
        aria-label={t('dashboard.trendAriaLabel')}
      >
        <path d={areaPath} fill={token.colorPrimary} opacity={0.12} stroke="none" />
        <path
          d={path}
          fill="none"
          stroke={token.colorPrimary}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {hoveredPoint ? (
          <>
            <line
              x1={hoveredPoint.x}
              x2={hoveredPoint.x}
              y1={0}
              y2={CHART_HEIGHT}
              stroke={token.colorBorder}
              strokeWidth={1}
              strokeDasharray="2 2"
            />
            <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={3.5} fill={token.colorPrimary} />
          </>
        ) : null}
      </svg>

      {hovered ? (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            background: token.colorBgElevated,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadiusSM,
            padding: '2px 8px',
            fontSize: 12,
            boxShadow: token.boxShadowSecondary,
            pointerEvents: 'none',
          }}
        >
          <Typography.Text strong style={{ fontSize: 12 }}>
            {hovered.value}
          </Typography.Text>{' '}
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {formatDateTime(new Date(hovered.at).toISOString())}
          </Typography.Text>
        </div>
      ) : (
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {t('dashboard.trendRange', { min: minValue, max: maxValue })}
        </Typography.Text>
      )}
    </div>
  )
}
